import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AccessType, ProfileSite, RegistrationSource } from '@ffd/api-client';
import { pathParameter } from '@libs/shared-services';
import {
  authActions,
  selectCurrentUserProfile,
  selectIsUserLoggedIn,
  selectIsUserLoggedOut,
  selectRecentRealSiteId,
  selectUserSites,
} from '@libs/store';
import { routes } from '@libs/utils';
import { Store } from '@ngrx/store';
import { filter, map, Observable, take, tap, withLatestFrom } from 'rxjs';
import { Platform } from '@angular/cdk/platform';
import { isMobileOrTablet } from '@libs/ui-lib';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly platform = inject(Platform);

  public isLoggedIn$(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.store.select(selectIsUserLoggedIn).pipe(
      tap((isLoggedIn) => {
        if (!isLoggedIn) {
          this.store.dispatch(authActions.onProfileRequested());
        }
      }),
      filter((loggedIn): loggedIn is boolean => loggedIn !== null),
      map((isLoggedIn) => {
        if (!isLoggedIn) {
          const isSetupFlow = state.url.includes(routes.setup);
          const isSiteAdminFlow = state.url.includes(routes.siteAdmin);

          const queryParams = {
            redirectUrl: state.url,
            isSetupFlow: isSetupFlow ? true : undefined,
            isSiteAdminFlow: isSiteAdminFlow ? true : undefined,
          };

          if (isMobileOrTablet(this.platform) && !next.paramMap.has(pathParameter('siteId'))) {
            this.router.navigate([routes.getTheApp]);
            return false;
          }

          if (state.url === '/setup') {
            this.router.navigate([routes.login, routes.loginSetup], { queryParams: queryParams });
          } else if (state.url === `/${routes.ffdNotes}`) {
            this.router.navigate([routes.ffdNotes, routes.login]);
          } else {
            this.router.navigate([routes.login], { queryParams: queryParams });
          }

          return false;
        }
        return true;
      }),
    );
  }

  public isLoggedOut$(): Observable<boolean> {
    return this.store.select(selectIsUserLoggedOut).pipe(
      filter((loggedOut): loggedOut is boolean => loggedOut !== null),
      map((isLoggedOut) => {
        if (!isLoggedOut) {
          this.router.navigate([routes.site]);
          return false;
        }
        return true;
      }),
    );
  }

  public hasSiteAccess$(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.store.select(selectUserSites).pipe(
      filter((sites): sites is ProfileSite[] => sites.length > 0),
      map((sites) => {
        const siteId = next.paramMap.get(pathParameter('siteId'));
        const site = sites.find((site) => site.id === siteId);

        if (siteId && !site) {
          this.router.navigate([routes.site], { queryParamsHandling: 'preserve' });
          return false;
        }

        const isUserExternalProviderOrPatient =
          site?.provider?.accessType === AccessType.COLLAB_ONLY_ACCESS ||
          site?.staff?.accessType === AccessType.COLLAB_ONLY_ACCESS ||
          !!site?.patient;

        if (isUserExternalProviderOrPatient) {
          const nextRoute = routes.allCollaborationsOnSite(siteId!);

          // we navigate to a child route and this prevents the guard to trigger indefinitely
          if (state.url === `/${nextRoute}`) {
            return true;
          }

          this.router.navigate([nextRoute]);
          return false;
        }

        return true;
      }),
    );
  }

  public redirectToDefaultSite$(next: ActivatedRouteSnapshot): Observable<boolean> {
    return this.store.select(selectCurrentUserProfile).pipe(
      filter((profile) => !!profile && profile.sites.length > 0),
      withLatestFrom(this.store.select(selectRecentRealSiteId)),
      tap(([profile, recentRealSiteId]) => {
        const { showcaseSiteId, site } = routes;

        const recentRealSiteIdInSites = profile!.sites.find(
          (site) =>
            site.id === recentRealSiteId &&
            (site.provider?.accessType === AccessType.FULL_ACCESS || site.staff?.accessType === AccessType.FULL_ACCESS),
        )?.id;
        const firstInternalSite = profile!.sites.find(
          (s) =>
            (s.provider?.accessType === AccessType.FULL_ACCESS || s.staff?.accessType === AccessType.FULL_ACCESS) &&
            s.id !== showcaseSiteId,
        );
        const isSiteAssociationInProgress = next.queryParams['siteAssociationToken'];

        // if the recent site is stored in the local storage, we default to that site if it exists
        // if not, we default to the first internal site, if it exists
        const defaultSiteId =
          recentRealSiteIdInSites ??
          (firstInternalSite && !isSiteAssociationInProgress
            ? firstInternalSite.id
            : profile!.sites.find((site) => site.id !== showcaseSiteId)?.id);

        this.router.navigate([site, defaultSiteId ?? showcaseSiteId], { queryParamsHandling: 'preserve' });
      }),
      map(() => true),
    );
  }

  public isGlobalAdmin$ = (): Observable<boolean> => {
    return this.store.select(selectCurrentUserProfile).pipe(
      map((user) => {
        if (user && user.isGlobalAdmin) {
          return true;
        } else {
          this.router.navigate(['/']);
          return false;
        }
      }),
    );
  };

  public ffdCoreGuard$ = (): Observable<boolean> => {
    const store = inject(Store);
    const router = inject(Router);

    return store.select(selectCurrentUserProfile).pipe(
      filter((profile) => !!profile),
      take(1),
      map((profile) => {
        if (profile?.registrationSource === RegistrationSource.FFD_CORE) {
          return true;
        }

        // If not FFD Core, redirect to default site route
        router.navigate([routes.ffdNotes]);
        return false;
      }),
    );
  };

  public ffdExpressGuard$ = (): Observable<boolean> => {
    const store = inject(Store);
    const router = inject(Router);

    return store.select(selectCurrentUserProfile).pipe(
      filter((profile) => !!profile),
      take(1),
      map((profile) => {
        if (profile?.registrationSource === RegistrationSource.FFD_EXPRESS) {
          return true;
        }

        // If not FFD Express, redirect to default site route
        router.navigate([routes.site]);
        return false;
      }),
    );
  };
}
